import { z } from "zod";

interface Viewport {
  top: number;
  height: number;
  width: number;
  left?: number;
}

/** 常驻按钮占的那一圈：收起态整体高度加一点余量。 */
const LAUNCHER_BAND_PX = 126;
const LAUNCHER_SIZE_PX = 56;
/** 拖动的入口不进店铺顶部导航这一条。 */
const LAUNCHER_TOP_PX = 76;

export type LauncherSide = "left" | "right";

/** 视口坐标下入口的左上角。 */
export interface LauncherPoint {
  x: number;
  y: number;
}

/** 买家拖出来的位置：停靠边加纵向比例，换视口尺寸时按比例还原。 */
export interface LauncherPlacement {
  side: LauncherSide;
  /** 0 贴顶部导航下沿，1 贴底部边距。 */
  ratio: number;
}

export interface LauncherBounds {
  width: number;
  height: number;
  horizontal: number;
  bottom: number;
  /** 避让吸底控件后入口离底边的最小距离，不小于 `bottom`。 */
  floor: number;
}

const launcherPlacementSchema = z.object({
  side: z.enum(["left", "right"]),
  ratio: z.number().min(0).max(1),
});

export function readLauncherPlacement(
  key: string,
): LauncherPlacement | undefined {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
    return launcherPlacementSchema.safeParse(value).data;
  } catch {
    return undefined;
  }
}

function launcherTravel(bounds: LauncherBounds): number {
  return bounds.height - LAUNCHER_SIZE_PX - LAUNCHER_TOP_PX - bounds.bottom;
}

/** 入口离视口底边的距离。比例按商家底距换算，避让只往上顶，避让条收起后回到原高度。 */
export function launcherOffset(ratio: number, bounds: LauncherBounds): number {
  const offset =
    bounds.bottom + (1 - ratio) * Math.max(0, launcherTravel(bounds));
  return Math.round(
    Math.max(
      bounds.floor,
      Math.min(bounds.height - LAUNCHER_SIZE_PX - LAUNCHER_TOP_PX, offset),
    ),
  );
}

/** 左右按入口中心落在视口哪一半，上下夹在导航和底部之间；压到底部下限记成 1，避让条收起后贴回底边。 */
export function snapLauncher(
  point: LauncherPoint,
  bounds: LauncherBounds,
): LauncherPlacement {
  const side =
    point.x + LAUNCHER_SIZE_PX / 2 < bounds.width / 2 ? "left" : "right";
  const travel = launcherTravel(bounds);
  if (travel <= 0 || point.y >= bounds.height - LAUNCHER_SIZE_PX - bounds.floor)
    return { side, ratio: 1 };
  return { side, ratio: Math.max(0, (point.y - LAUNCHER_TOP_PX) / travel) };
}

export function launcherPoint(
  { side, ratio }: LauncherPlacement,
  bounds: LauncherBounds,
): LauncherPoint {
  return {
    x:
      side === "left"
        ? bounds.horizontal
        : bounds.width - bounds.horizontal - LAUNCHER_SIZE_PX,
    y: bounds.height - LAUNCHER_SIZE_PX - launcherOffset(ratio, bounds),
  };
}

/**
 * 只有钉在视口上的元素才算遮挡。随页面滚动的加购按钮会自己滚走，把它当遮挡会让
 * 挂件在滚动途中被顶上去又落回来；试点主题首页就有这样一个按钮。
 */
export function isViewportPinned(element: Element): boolean {
  // 只查到 body 之前：iOS 抽屉打开时 body 被固定成店面的滚动容器（主题自己的滚动锁也这样），
  // 里面的加购按钮照样跟着页面滚；算进去的话入口会被抬到页面中间的按钮上方。
  const { body } = element.ownerDocument;
  for (
    let node: Element | null = element;
    node && node !== body;
    node = node.parentElement
  ) {
    const { position } = getComputedStyle(node);
    if (position === "fixed" || position === "sticky") return true;
  }
  return false;
}

/**
 * 常驻按钮要给固定吸底的购买控件和 Cookie 提示让位（PRD 4.1）。只量按钮自己占的
 * 那一圈：展开的面板是买家点开的浮层，盖住页面是它该干的事，不参与避让。
 */
export function readObstructionBottom(
  rects: DOMRect[],
  viewport: Viewport,
  position: LauncherSide,
  spacing = { horizontal: 16, bottom: 16 },
): number {
  const viewportBottom = viewport.top + viewport.height;
  const edge =
    position === "left"
      ? spacing.horizontal
      : viewport.width - spacing.horizontal;
  const launcherBottom = viewportBottom - spacing.bottom;
  const top = launcherBottom - LAUNCHER_BAND_PX;
  let bottom = 0;
  for (const rect of rects) {
    const overlaps =
      position === "left"
        ? rect.right > edge && rect.left < edge + 340
        : rect.left < edge && rect.right > edge - 340;
    if (
      rect.width &&
      rect.height &&
      overlaps &&
      rect.bottom > top &&
      rect.top < launcherBottom
    ) {
      bottom = Math.max(bottom, viewportBottom - rect.top + 12);
    }
  }
  return bottom;
}
